/**
 * Phase 3u — Catalog detail sheet.
 *
 * Full-screen view of one catalogued voice: the header restates
 * identity + status, every session this voice appeared in renders
 * its own conversation block with the voice's turns inline, and a
 * "Name this voice" action retroactively attributes the entire
 * history to a chosen contact.
 *
 * This is the surface that rescues the user from losing content
 * when they dismiss a provisional prompt — the transcripts are
 * still here, and naming the voice converts them into a proper
 * contact interaction history.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, Loader2, MapPin, User, UserPlus, X } from "lucide-react";

import { useContacts } from "@/hooks/useContacts";
import {
  adaptUserSelfFromSpeaker,
  devListUnknownVoiceTurns,
  resolveUnknownVoice,
} from "@/hooks/useDevDiagnostics";
import { getFullName } from "@/lib/data";
import type {
  DevUnknownVoice,
  DevUnknownVoiceTurnGroup,
} from "@/types/devMode";

import { formatRelativeTime, formatAbsoluteTime } from "./devFormatters";

interface Props {
  voice: DevUnknownVoice;
  onClose: () => void;
  onResolved: () => void;
}

export function CatalogDetailSheet({ voice, onClose, onResolved }: Props) {
  const [groups, setGroups] = useState<DevUnknownVoiceTurnGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const { contacts } = useContacts();

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const rows = await devListUnknownVoiceTurns(voice.id);
      setGroups(rows);
    } catch (err) {
      setError(String(err));
    }
  }, [voice.id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const totalTurns = useMemo(
    () => groups?.reduce((acc, g) => acc + g.turns.length, 0) ?? 0,
    [groups]
  );

  const isResolved = voice.resolvedContactId !== null;
  const identity = identityFor(voice.displayLabel ?? voice.id);

  // Resolved voices read under their true identity — "You" for
  // user_self, the contact's full name for a named person — so the
  // detail header reflects the user's mental model rather than the
  // generic Speaker A/B shorthand.
  const resolvedTitle = (() => {
    if (voice.resolvedContactId === "user_self") return "You";
    if (voice.resolvedContactId) {
      const c = contacts.find(
        (x) =>
          x.appleContactId === voice.resolvedContactId ||
          x.id === voice.resolvedContactId
      );
      if (c) return getFullName(c) || "Unnamed contact";
    }
    return null;
  })();
  const headerTitle = resolvedTitle ?? voice.displayLabel ?? "Unlabeled";

  return (
    <div className="fixed inset-0 z-[10100] flex flex-col bg-black text-white">
      <header
        className="flex items-center gap-3 border-b border-white/10 bg-black px-4"
        style={{
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
          paddingBottom: "12px",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Back"
          className="rounded-full border border-white/10 p-2 text-white/70 active:scale-95"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="flex flex-1 flex-col">
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/30">
            Catalog voice
          </p>
          <h2 className="text-[18px] font-semibold text-white">
            {headerTitle}
          </h2>
          {resolvedTitle && voice.displayLabel && (
            <p className="text-[11px] text-white/40">was {voice.displayLabel}</p>
          )}
        </div>
      </header>

      <div
        className="relative flex-1 overflow-y-auto"
        style={{
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)",
        }}
      >
        {/* Identity banner — ambient color pulled from the voice's
            hash-derived hue so this sheet feels like a specific
            person even before they have a name. */}
        <div
          className="relative overflow-hidden border-b border-white/5"
          style={{
            background: `radial-gradient(120% 80% at 50% 0%, ${identity.glow}, transparent 65%)`,
          }}
        >
          <div className="flex flex-col items-center gap-3 px-4 py-6">
            <div
              className="flex h-16 w-16 items-center justify-center rounded-full border text-[20px] font-semibold tracking-tight text-white"
              style={{
                borderColor: identity.ring,
                backgroundColor: identity.fill,
                textShadow: "0 0 12px rgba(0,0,0,0.45)",
              }}
            >
              {identity.letter}
            </div>
            <div className="flex flex-col items-center gap-0.5">
              <p className="text-[13px] text-white/70">
                Heard {voice.occurrenceCount}×
                {voice.totalSpeechSeconds > 0 && (
                  <> · {humanDuration(voice.totalSpeechSeconds)} of speech</>
                )}
              </p>
              <p className="text-[11px] text-white/40">
                First {formatRelativeTime(voice.firstSeenAt)} · last{" "}
                {formatRelativeTime(voice.lastSeenAt)}
              </p>
            </div>
          </div>
        </div>

        {error && (
          <p className="p-4 text-sm text-red-400">Failed to load: {error}</p>
        )}

        {!groups && !error && (
          <div className="flex items-center gap-2 p-4 text-sm text-white/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading conversations…
          </div>
        )}

        {groups && groups.length === 0 && (
          <p className="p-6 text-center text-[12px] text-white/40">
            No transcripts associated with this voice yet.
          </p>
        )}

        {groups && groups.length > 0 && (
          <section className="flex flex-col gap-3 p-4">
            <p className="text-[10px] uppercase tracking-[0.22em] text-white/30">
              {groups.length} conversation{groups.length === 1 ? "" : "s"} ·{" "}
              {totalTurns} turn{totalTurns === 1 ? "" : "s"}
            </p>
            {groups.map((g) => (
              <ConversationBlock
                key={`${g.sessionId}-${g.clusterLocalId}`}
                group={g}
                identity={identity}
                displayLabel={voice.displayLabel ?? "Speaker"}
              />
            ))}
          </section>
        )}
      </div>

      {/* Sticky bottom action — the whole point of this sheet. Hidden
          once the voice is resolved; the banner in the body confirms
          the link to a contact. */}
      {!isResolved && (
        <footer
          className="absolute inset-x-0 bottom-0 border-t border-white/10 bg-black/95 px-4 backdrop-blur"
          style={{
            paddingTop: "12px",
            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
          }}
        >
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-yellow-400 py-3 text-[14px] font-semibold text-black active:opacity-80"
          >
            <UserPlus className="h-4 w-4" />
            Name this voice
          </button>
        </footer>
      )}

      {pickerOpen && (
        <ContactPicker
          onCancel={() => setPickerOpen(false)}
          onPick={async (contactId) => {
            try {
              await resolveUnknownVoice(voice.id, contactId);
              setPickerOpen(false);
              onResolved();
            } catch (err) {
              setError(String(err));
              setPickerOpen(false);
            }
          }}
          onPickSelf={async () => {
            try {
              await adaptUserSelfFromSpeaker(voice.id);
              setPickerOpen(false);
              onResolved();
            } catch (err) {
              setError(String(err));
              setPickerOpen(false);
            }
          }}
        />
      )}
    </div>
  );
}

// MARK: - Conversation block

function ConversationBlock({
  group,
  identity,
  displayLabel,
}: {
  group: DevUnknownVoiceTurnGroup;
  identity: Identity;
  displayLabel: string;
}) {
  const sessionTitle = group.sessionStartedAt
    ? formatRelativeTime(group.sessionStartedAt)
    : formatRelativeTime(group.seenAt);
  const durationLabel = group.sessionDurationSeconds
    ? humanDuration(group.sessionDurationSeconds)
    : null;

  return (
    <div className="overflow-hidden rounded-xl border border-white/10 bg-white/[0.02]">
      <div className="flex items-baseline justify-between gap-3 border-b border-white/5 px-3 py-2">
        <div className="flex flex-col">
          <span className="text-[13px] font-medium text-white">
            {sessionTitle}
          </span>
          <span className="text-[10px] text-white/40">
            {formatAbsoluteTime(group.sessionStartedAt ?? group.seenAt)}
          </span>
        </div>
        <div className="flex items-baseline gap-2 text-[10px] text-white/50">
          {durationLabel && <span>{durationLabel} session</span>}
          <span>·</span>
          <span>{humanDuration(group.speechSeconds)} spoken</span>
        </div>
      </div>
      {group.sessionLocationLabel && (
        <div className="flex items-center gap-1.5 px-3 pt-2 text-[10px] text-white/40">
          <MapPin className="h-3 w-3" />
          {group.sessionLocationLabel}
        </div>
      )}
      <div className="flex flex-col gap-2 px-3 py-3">
        {group.turns.length === 0 ? (
          <p className="text-[11px] italic text-white/30">
            No transcript captured for this session.
          </p>
        ) : (
          group.turns.map((turn) => (
            <TurnBubble
              key={turn.id}
              turn={turn}
              identity={identity}
              displayLabel={displayLabel}
            />
          ))
        )}
      </div>
    </div>
  );
}

function TurnBubble({
  turn,
  identity,
  displayLabel,
}: {
  turn: {
    id: string;
    startSeconds: number;
    endSeconds: number;
    transcript: string | null;
    speakerLabel: string | null;
  };
  identity: Identity;
  displayLabel: string;
}) {
  const label = turn.speakerLabel ?? displayLabel;
  const text = turn.transcript?.trim();
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline gap-2">
        <span
          className="text-[11px] font-medium"
          style={{ color: identity.chip }}
        >
          {label}
        </span>
        <span className="font-mono text-[9px] text-white/25">
          {formatTimestamp(turn.startSeconds)}
        </span>
      </div>
      <p
        className={`whitespace-pre-wrap text-[13px] leading-relaxed ${
          text ? "text-white/85" : "italic text-white/30"
        }`}
      >
        {text ?? "(no transcript for this turn)"}
      </p>
    </div>
  );
}

// MARK: - Contact picker

function ContactPicker({
  onPick,
  onPickSelf,
  onCancel,
}: {
  onPick: (contactId: string) => void;
  onPickSelf: () => void;
  onCancel: () => void;
}) {
  const { contacts, loading } = useContacts();
  const [query, setQuery] = useState("");
  const [confirming, setConfirming] = useState(false);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) => {
      const name = `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim().toLowerCase();
      return name.includes(q);
    });
  }, [contacts, query]);

  const selfMatchesQuery = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return "this is me".includes(q) || "you".includes(q) || "me".includes(q);
  }, [query]);

  return (
    <div className="fixed inset-0 z-[10200] flex flex-col bg-black">
      <header
        className="flex items-center gap-3 border-b border-white/10 bg-black px-4"
        style={{
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
          paddingBottom: "12px",
        }}
      >
        <button
          type="button"
          onClick={onCancel}
          aria-label="Close picker"
          className="rounded-full border border-white/10 p-2 text-white/70 active:scale-95"
        >
          <X className="h-4 w-4" />
        </button>
        <h2 className="text-[16px] font-semibold text-white">Who was this?</h2>
      </header>
      <div className="p-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search contacts…"
          className="w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-[14px] text-white placeholder-white/30 outline-none focus:border-yellow-400/60"
        />
      </div>
      <div
        className="flex-1 overflow-y-auto"
        style={{
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
        }}
      >
        {selfMatchesQuery && (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="flex w-full items-center gap-3 border-b border-white/10 bg-yellow-400/[0.06] px-4 py-3 text-left active:bg-yellow-400/[0.12]"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full border border-yellow-400/40 bg-yellow-400/10">
              <User className="h-4 w-4 text-yellow-300" />
            </span>
            <span className="flex flex-col">
              <span className="text-[14px] font-medium text-yellow-100">
                This is me
              </span>
              <span className="text-[11px] text-yellow-200/50">
                Merge into your voiceprint
              </span>
            </span>
          </button>
        )}
        {loading && (
          <div className="flex items-center gap-2 p-4 text-sm text-white/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading contacts…
          </div>
        )}
        {!loading && filtered.length === 0 && !selfMatchesQuery && (
          <p className="p-6 text-center text-[12px] text-white/40">
            No contacts match.
          </p>
        )}
        <ul className="flex flex-col">
          {filtered.map((c) => {
            const appleId = c.appleContactId ?? c.id;
            const name =
              `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim() || "Unnamed";
            return (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => onPick(appleId)}
                  className="flex w-full items-center justify-between border-b border-white/5 px-4 py-3 text-left active:bg-white/[0.02]"
                >
                  <span className="text-[14px] text-white">{name}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {confirming && (
        <div
          className="absolute inset-0 z-[10300] flex items-center justify-center bg-black/80 p-6"
          onClick={() => setConfirming(false)}
        >
          <div
            className="flex w-full max-w-xs flex-col gap-3 rounded-2xl border border-white/10 bg-neutral-950 p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-[15px] font-semibold text-white">
              Merge this voice into you?
            </h3>
            <p className="text-[12px] leading-relaxed text-white/60">
              Every turn this voice owned across every session will be
              reassigned to you, and your voiceprint gains a new sample so
              future sessions recognize you more easily.
            </p>
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="flex-1 rounded-lg border border-white/15 py-2 text-[13px] text-white/80 active:opacity-70"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirming(false);
                  onPickSelf();
                }}
                className="flex-1 rounded-lg bg-yellow-400 py-2 text-[13px] font-semibold text-black active:opacity-80"
              >
                Yes, merge
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// MARK: - Helpers (mirrors CatalogTab)

interface Identity {
  letter: string;
  hue: number;
  ring: string;
  fill: string;
  glow: string;
  chip: string;
}

function identityFor(seed: string): Identity {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) & 0xffffffff;
  }
  const hue = Math.abs(hash) % 360;
  const match = seed.match(/([A-Z]+)$/i);
  const letter = (match ? match[1] : seed[0] ?? "?").slice(0, 2).toUpperCase();
  return {
    letter,
    hue,
    ring: `hsla(${hue}, 70%, 65%, 0.45)`,
    fill: `hsla(${hue}, 55%, 22%, 1)`,
    glow: `hsla(${hue}, 75%, 55%, 0.25)`,
    chip: `hsla(${hue}, 75%, 72%, 0.9)`,
  };
}

function humanDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0s";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const rem = Math.round(seconds - minutes * 60);
  return rem === 0 ? `${minutes}m` : `${minutes}m ${rem}s`;
}

function formatTimestamp(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds - m * 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}
